import React from "react";
import * as S from "../shared/styles";
import { SourceColumnSelect } from "./FieldBuilder";
import { SourceColumnsState } from "./useSourceColumns";

// ----------------------------  FlagColumnPicker  ----------------------------
// Picks the boolean sample columns that switch a view on: one dropdown per picked
// column (clear it or click the cross to drop it) and a trailing dropdown to add one.

interface FlagColumnPickerProps {
  value: string[];
  onChange: (columns: string[]) => void;
  sourceColumns: SourceColumnsState;
}

const FlagColumnPicker: React.FC<FlagColumnPickerProps> = ({ value, onChange, sourceColumns }) => {
  const flagColumns = sourceColumns.columns.filter((c) => c.type === "boolean");
  const replace = (i: number, column: string) =>
    onChange(
      column ? value.map((c, k) => (k === i ? column : c)) : value.filter((_, k) => k !== i),
    );
  const canAdd =
    !sourceColumns.loading &&
    !sourceColumns.error &&
    flagColumns.some((c) => value.indexOf(c.value) === -1);

  return (
    <div>
      <label style={S.formLabel}>Show only where one of these flag columns is true</label>
      {sourceColumns.loading && <div style={S.formHelp}>Loading columns...</div>}
      {sourceColumns.error && (
        <div style={S.formError}>
          Could not load the sample columns. Reload the page to pick flag columns.
        </div>
      )}
      {!sourceColumns.loading && !sourceColumns.error && flagColumns.length === 0 && (
        <div style={S.formHelp}>The samples have no flag (true / false) columns.</div>
      )}
      {value.map((column, i) => (
        <div key={column} style={pickRow}>
          <SourceColumnSelect
            value={column}
            columns={flagColumns}
            taken={value.filter((_, k) => k !== i)}
            onChange={(v) => replace(i, v)}
            ariaLabel={"Flag column " + (i + 1)}
          />
          <button
            style={S.fieldCardRemoveBtn}
            onClick={() => onChange(value.filter((_, k) => k !== i))}
            title="Remove"
          >
            ✕
          </button>
        </div>
      ))}
      {canAdd && (
        <div style={pickRow}>
          <SourceColumnSelect
            value=""
            columns={flagColumns}
            taken={value}
            onChange={(v) => v && onChange([...value, v])}
            ariaLabel="Add a flag column"
            placeholder={value.length ? "+ Add another flag column" : "Select a flag column"}
          />
        </div>
      )}
    </div>
  );
};

// ----------------------------  Styles  ----------------------------

const pickRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  marginBottom: "6px",
};

export default FlagColumnPicker;
